import React, { useRef, useEffect } from "react";
import { useFormContext, useController } from "react-hook-form";
import { get } from "lodash";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

interface InputFieldProps {
  name: string;
  label: string;
  required?: boolean;
  isAsterisk?: boolean;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  requiredMessage?: string;
  value?: string;
  validation?: {
    required?: string | boolean;
    min?: number | { value: number; message: string };
    max?: number | { value: number; message: string };
    pattern?: { value: RegExp; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;

  // Restriction props
  onlyAlphabets?: boolean;
  onlyNumeric?: boolean;
  allowDecimal?: boolean;
  allowSpecialChars?: boolean; //  Naya prop
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  value,
  isAsterisk = true,
  required = false,
  placeholder,
  type = "text",
  requiredMessage = "This field is required",
  validation = {},
  className = "",
  disabled = false,

  onlyAlphabets = false,
  onlyNumeric = false,
  allowDecimal = false,
  allowSpecialChars = false, // by default block special chars

  min,
  max,
  ...rest
}) => {
  const {
    control,
    formState: { isSubmitted },
    setError,
    clearErrors,
  } = useFormContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isNumericMode = type === "number" || onlyNumeric;

  const {
    field: { onChange, value: fieldValue, ...fieldRest },
    fieldState: { isTouched, error },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      ...validation,
    },
    defaultValue: value,
  });

  const errorMessage =
    isSubmitted || error?.type === "maxLength" || error?.type === "max"
      ? (error?.message as string | undefined)
      : undefined;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Clear any existing timeout immediately on new input
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Check for maxLength in validation or rest
    const maxLengthRule = validation?.maxLength;
    const maxLength =
      maxLengthRule?.value || maxLengthRule || (rest as any).maxLength;

    if (maxLength && rawValue.length > maxLength) {
      // Manual trigger error
      const message =
        maxLengthRule?.message ||
        `Maximum length of ${maxLength} characters reached`;
      setError(name, { type: "maxLength", message });

      timeoutRef.current = setTimeout(() => {
        clearErrors(name);
      }, 1000);

      return; // Block input
    }

    // Check for max value limit if type is numeric
    if (isNumericMode && max !== undefined && rawValue !== "") {
      const numValue = parseFloat(rawValue);
      if (!isNaN(numValue) && numValue > Number(max)) {
        // Optional: Trigger error or just block
        setError(name, {
          type: "max",
          message: `Maximum value of ${max} exceeded`,
        });
        timeoutRef.current = setTimeout(() => {
          clearErrors(name);
        }, 1000);
        return; // Block input exceeding max
      }
    }

    // If typing valid input, clear specific errors
    if (error?.type === "maxLength" || error?.type === "max") {
      clearErrors(name);
    }

    onChange(e);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const allowedControlKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
    ];

    // Ctrl+C, Ctrl+V, Ctrl+A, Ctrl+Z, Cmd+ shortcuts etc.
    if (e.ctrlKey || e.metaKey) return;

    // Allow Control Keys
    if (allowedControlKeys.includes(key)) return;

    // ---------- ONLY ALPHABETS ----------
    if (onlyAlphabets) {
      const isAlphabet = /^[a-zA-Z]$/.test(key);
      const isSpace = key === " ";
      if (!isAlphabet && !isSpace) {
        e.preventDefault();
      }
      return;
    }

    // ---------- ONLY NUMERIC ----------
    if (isNumericMode) {
      const isNumber = /^[0-9]$/.test(key);
      const isDot = key === ".";

      // Decimal handling
      if (isDot) {
        if (!allowDecimal) {
          e.preventDefault();
          return;
        }
        // Prevent multiple dots
        if ((e.target as HTMLInputElement).value.includes(".")) {
          e.preventDefault();
          return;
        }
      }

      if (!isNumber && !isDot) {
        e.preventDefault();
      }
      return;
    }

    // ---------- SPECIAL CHARACTERS BLOCK (default) ----------
    if (!onlyAlphabets && !isNumericMode && type !== "email") {
      const isAlphanumeric = /^[a-zA-Z0-9]$/.test(key);
      const isSpace = key === " ";

      const isSpecialChar = /[!@#$%^&*()_+\=\[\]{};':"\\|,.<>\/?`~]/.test(key);

      if (isSpecialChar && !allowSpecialChars) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label}{" "}
          {isAsterisk && required && <span className="text-danger">*</span>}
        </label>
      )}
      <input
        type={isNumericMode ? "text" : type}
        inputMode={isNumericMode ? "decimal" : undefined}
        value={fieldValue ?? ""}
        className={`form-control ${errorMessage ? "is-invalid" : ""}`}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        {...fieldRest}
        {...rest}
      />
      {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
    </div>
  );
};

export default InputField;
